/**
 * Mock Data Service
 * 
 * Provides realistic mock data for local development and testing.
 * This service is activated when isLocalEnvironment() returns true.
 */

import { GetRatesAndRosterResponse, RatesData } from '../pages/rosterAndRates/model';
import { GetTargetAndWIPVolumeResponse } from '../pages/volumesAndWip/model';
import { SPMSGetShiftPlanResponse } from '../types/shiftPlan';
import {
  GetWorkItemCountsResponse,
  GetWorkItemsResponse,
  WorkItemSummary,
} from '../pages/workItems/model';
import {
  LoadWorkItemResponse,
  UpdateWorkItemStateRequest,
  UpdateWorkItemStateResponse,
  WorkItem,
} from '../pages/workItemDetail/model';
import { PlanStatus, WORK_ITEMS_PAGE_SIZE } from '../utils/constants';
import { GetShiftRosterResponse, ShiftRosterEmployee } from '../pages/stationAssignment/shiftRoster/models';
import {
  AssignmentAssociate,
  AssignmentsMetrics,
  GetAssignmentsResponse,
  ProcessPath,
  Workstation,
} from '../pages/stationAssignment/assignments/models';

// Request parameter interfaces for dynamic mock data
export interface MockRequestParams {
  warehouseId?: string;
  nodeId?: string;
  planId?: string;
  ofdDate?: string;
  [key: string]: any;
}

// Warehouse-specific data variations
const warehouseVariations: { [key: string]: any } = {
  'LAS2': {
    nodeId: 'LAS2',
    name: 'Las Vegas 2',
    baseVolume: 125000,
    associateCount: 260,
    efficiency: 0.85,
    rates: { inbound: 25.5, outbound: 45.8, support: 15.0 }
  },
  'DFW1': {
    nodeId: 'DFW1', 
    name: 'Dallas Fort Worth 1',
    baseVolume: 150000,
    associateCount: 320,
    efficiency: 0.88,
    rates: { inbound: 27.2, outbound: 48.5, support: 16.5 }
  },
  'SEA4': {
    nodeId: 'SEA4',
    name: 'Seattle 4', 
    baseVolume: 95000,
    associateCount: 210,
    efficiency: 0.82,
    rates: { inbound: 28.0, outbound: 52.0, support: 17.2 }
  },
  'default': {
    nodeId: 'LAS2',
    name: 'Default Warehouse',
    baseVolume: 125000,
    associateCount: 260, 
    efficiency: 0.85,
    rates: { inbound: 25.5, outbound: 45.8, support: 15.0 }
  }
};

// Real Production Rates Data — updated from live backend payload
export const mockRatesData: RatesData = {
  functionRates: [
    { processPath: 'C-Returns Processed', functionId: '4300016842', functionName: 'WHD RUG', functionType: 'DIRECT', rate: 8.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    { processPath: 'C-Returns Processed', functionId: '4300000047', functionName: 'Customer Returns', functionType: 'DIRECT', rate: 12.3, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    { processPath: 'C-Returns Processed', functionId: '1667843456854', functionName: 'Primary Grading', functionType: 'DIRECT', rate: 18.0, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    { processPath: 'C-Returns Processed', functionId: '4300016841', functionName: 'WHD TV', functionType: 'DIRECT', rate: 11.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    { processPath: 'C-Returns Support', functionId: '4300016758', functionName: 'C-Returns Refurbish', functionType: 'DIRECT', rate: 15.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '1599234818280', functionName: 'T-Mech Water Spider', functionType: 'DIRECT', rate: 5.5, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300018934', functionName: 'C-Return Audit', functionType: 'DIRECT', rate: 9.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300018932', functionName: 'C-Return Sort', functionType: 'DIRECT', rate: 3.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '1599234929207', functionName: 'Team_Mech_Audit', functionType: 'DIRECT', rate: 9.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300006654', functionName: 'C-Returns Prob Solve', functionType: 'DIRECT', rate: 8.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300006653', functionName: 'C-Returns Lead/PA', functionType: 'DIRECT', rate: 3.0, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300006651', functionName: 'C-Returns Ambassador', functionType: 'DIRECT', rate: 0.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '1599234892624', functionName: 'Team_Mech_P-Solver', functionType: 'DIRECT', rate: 8.3, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Stow', functionId: '4300006823', functionName: 'Stow C Returns', functionType: 'DIRECT', rate: 43.2, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Stow' },
    { processPath: 'AR Grading', functionId: '1667843456854', functionName: 'Primary Grading', functionType: 'DIRECT', rate: 84.03421998566397, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
    { processPath: 'AR Grading', functionId: '4300006832', functionName: 'Technical HL Grading', functionType: 'DIRECT', rate: 22.038745734116777, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
    { processPath: 'AR Grading', functionId: '4300006862', functionName: 'WD Grading - AR', functionType: 'DIRECT', rate: 14.3, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
    { processPath: 'C-Returns Clean Decant Processed', functionId: '1617400138427', functionName: 'Function', functionType: 'DIRECT', rate: 22.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Clean Decant Processed' },
    { processPath: 'AR Grading Support', functionId: '4300006896', functionName: 'WD Problem Solve', functionType: 'DIRECT', rate: 12.613712598091766, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading Support' },
    { processPath: 'AR Grading Support', functionId: '1718200607839', functionName: 'WD TL Support', functionType: 'DIRECT', rate: 0.2851684197771836, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading Support' },
    { processPath: 'AR Speciality Grading', functionId: '1617058767035', functionName: 'Spec WHD Grading 1', functionType: 'DIRECT', rate: 3.8724515809282, volume: 0.0, timeSeconds: 0.0, department: 'AR Speciality Grading' },
  ],
  processRates: [
    { processName: 'External Repairs', processType: 'PROCESS', rate: 150.287112, volume: 23.0, timeSeconds: 11444.0, department: 'Reverse Logistics' },
    { processName: 'IB Defect', processType: 'PROCESS', rate: 534.144384, volume: 2002.0, timeSeconds: 13388.0, department: 'Inbound' },
    { processName: 'Transfer Out', processType: 'PROCESS', rate: 1031.864004, volume: 22.0, timeSeconds: 0.0, department: 'DA - Building to Building Transfer' },
    { processName: 'V-Return', processType: 'PROCESS', rate: 146.26321199999998, volume: 502.0, timeSeconds: 54385.0, department: 'Reverse Logistics' },
    { processName: 'FC Summary', processType: 'PROCESS', rate: 8.464022345183578, volume: 2002.0, timeSeconds: 93349.0, department: 'FC Summary' },
    { processName: 'Stow to Prime', processType: 'PROCESS', rate: 1234.8300960000001, volume: 287.0, timeSeconds: 12202.0, department: 'Inbound' },
    { processName: 'Transfer In and Stow', processType: 'PROCESS', rate: 677.623896, volume: 2002.0, timeSeconds: 62432.0, department: 'Inbound' },
    { processName: 'Transfer Out Dock', processType: 'PROCESS', rate: 945.861588, volume: 22.0, timeSeconds: 2866.0, department: 'DA - Building to Building Transfer' },
    { processName: 'Warehouse Deals', processType: 'PROCESS', rate: 60.308496000000005, volume: 207.0, timeSeconds: 68372.0, department: 'Reverse Logistics' },
    { processName: 'C-Return', processType: 'PROCESS', rate: 407.28218400000003, volume: 4707.0, timeSeconds: 747668.0, department: 'Reverse Logistics' },
    { processName: 'RC Summary', processType: 'PROCESS', rate: 9.039636864430488, volume: 7233.0, timeSeconds: 985523.0, department: 'RC Summary' },
    { processName: 'TO Lead/PA', processType: 'PROCESS', rate: 12927.385584, volume: 22.0, timeSeconds: 5027.0, department: 'DA - Building to Building Transfer' },
    { processName: 'IB Lead/PA', processType: 'PROCESS', rate: 970.6650119999999, volume: 2002.0, timeSeconds: 5327.0, department: 'Inbound' },
    { processName: 'RSR', processType: 'PROCESS', rate: 44486.135604, volume: 7236.0, timeSeconds: 0.0, department: 'Inbound' }
  ],
  functionRatesByDepartment: {
    'C-Returns Processed': [
      { processPath: 'C-Returns Processed', functionId: '4300016842', functionName: 'WHD RUG', functionType: 'DIRECT', rate: 8.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
      { processPath: 'C-Returns Processed', functionId: '4300000047', functionName: 'Customer Returns', functionType: 'DIRECT', rate: 12.3, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
      { processPath: 'C-Returns Processed', functionId: '1667843456854', functionName: 'Primary Grading', functionType: 'DIRECT', rate: 18.0, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
      { processPath: 'C-Returns Processed', functionId: '4300016841', functionName: 'WHD TV', functionType: 'DIRECT', rate: 11.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    ],
    'C-Returns Support': [
      { processPath: 'C-Returns Support', functionId: '4300016758', functionName: 'C-Returns Refurbish', functionType: 'DIRECT', rate: 15.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '1599234818280', functionName: 'T-Mech Water Spider', functionType: 'DIRECT', rate: 5.5, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300018934', functionName: 'C-Return Audit', functionType: 'DIRECT', rate: 9.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300018932', functionName: 'C-Return Sort', functionType: 'DIRECT', rate: 3.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '1599234929207', functionName: 'Team_Mech_Audit', functionType: 'DIRECT', rate: 9.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300006654', functionName: 'C-Returns Prob Solve', functionType: 'DIRECT', rate: 8.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300006653', functionName: 'C-Returns Lead/PA', functionType: 'DIRECT', rate: 3.0, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300006651', functionName: 'C-Returns Ambassador', functionType: 'DIRECT', rate: 0.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '1599234892624', functionName: 'Team_Mech_P-Solver', functionType: 'DIRECT', rate: 8.3, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    ],
    'C-Returns Stow': [
      { processPath: 'C-Returns Stow', functionId: '4300006823', functionName: 'Stow C Returns', functionType: 'DIRECT', rate: 43.2, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Stow' },
    ],
    'AR Grading': [
      { processPath: 'AR Grading', functionId: '1667843456854', functionName: 'Primary Grading', functionType: 'DIRECT', rate: 84.03421998566397, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
      { processPath: 'AR Grading', functionId: '4300006832', functionName: 'Technical HL Grading', functionType: 'DIRECT', rate: 22.038745734116777, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
      { processPath: 'AR Grading', functionId: '4300006862', functionName: 'WD Grading - AR', functionType: 'DIRECT', rate: 14.3, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
    ],
    'C-Returns Clean Decant Processed': [
      { processPath: 'C-Returns Clean Decant Processed', functionId: '1617400138427', functionName: 'Function', functionType: 'DIRECT', rate: 22.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Clean Decant Processed' },
    ],
    'AR Grading Support': [
      { processPath: 'AR Grading Support', functionId: '4300006896', functionName: 'WD Problem Solve', functionType: 'DIRECT', rate: 12.613712598091766, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading Support' },
      { processPath: 'AR Grading Support', functionId: '1718200607839', functionName: 'WD TL Support', functionType: 'DIRECT', rate: 0.2851684197771836, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading Support' },
    ],
    'AR Speciality Grading': [
      { processPath: 'AR Speciality Grading', functionId: '1617058767035', functionName: 'Spec WHD Grading 1', functionType: 'DIRECT', rate: 3.8724515809282, volume: 0.0, timeSeconds: 0.0, department: 'AR Speciality Grading' },
    ],
  },
  processRatesByDepartment: {
    'FC Summary': [{ processName: 'FC Summary', processType: 'PROCESS', rate: 8.464022345183578, volume: 2002.0, timeSeconds: 93349.0, department: 'FC Summary' }],
    'Reverse Logistics': [
      { processName: 'External Repairs', processType: 'PROCESS', rate: 150.287112, volume: 23.0, timeSeconds: 11444.0, department: 'Reverse Logistics' },
      { processName: 'V-Return', processType: 'PROCESS', rate: 146.26321199999998, volume: 502.0, timeSeconds: 54385.0, department: 'Reverse Logistics' },
      { processName: 'Warehouse Deals', processType: 'PROCESS', rate: 60.308496000000005, volume: 207.0, timeSeconds: 68372.0, department: 'Reverse Logistics' },
      { processName: 'C-Return', processType: 'PROCESS', rate: 407.28218400000003, volume: 4707.0, timeSeconds: 747668.0, department: 'Reverse Logistics' }
    ],
    'DA - Building to Building Transfer': [
      { processName: 'Transfer Out', processType: 'PROCESS', rate: 1031.864004, volume: 22.0, timeSeconds: 0.0, department: 'DA - Building to Building Transfer' },
      { processName: 'Transfer Out Dock', processType: 'PROCESS', rate: 945.861588, volume: 22.0, timeSeconds: 2866.0, department: 'DA - Building to Building Transfer' },
      { processName: 'TO Lead/PA', processType: 'PROCESS', rate: 12927.385584, volume: 22.0, timeSeconds: 5027.0, department: 'DA - Building to Building Transfer' }
    ],
    'RC Summary': [{ processName: 'RC Summary', processType: 'PROCESS', rate: 9.039636864430488, volume: 7233.0, timeSeconds: 985523.0, department: 'RC Summary' }],
    'Inbound': [
      { processName: 'IB Defect', processType: 'PROCESS', rate: 534.144384, volume: 2002.0, timeSeconds: 13388.0, department: 'Inbound' },
      { processName: 'Stow to Prime', processType: 'PROCESS', rate: 1234.8300960000001, volume: 287.0, timeSeconds: 12202.0, department: 'Inbound' },
      { processName: 'Transfer In and Stow', processType: 'PROCESS', rate: 677.623896, volume: 2002.0, timeSeconds: 62432.0, department: 'Inbound' },
      { processName: 'IB Lead/PA', processType: 'PROCESS', rate: 970.6650119999999, volume: 2002.0, timeSeconds: 5327.0, department: 'Inbound' },
      { processName: 'RSR', processType: 'PROCESS', rate: 44486.135604, volume: 7236.0, timeSeconds: 0.0, department: 'Inbound' }
    ]
  }
};

// Real Production Roster and Rates Response — updated from live backend payload
export const mockRatesAndRoster: GetRatesAndRosterResponse = {
  rosterData: {
    headcount: {
      recommended: 271.0,
      vtoOpen: 0.0,
      vtoAccepted: 0.0,
      vetOpen: 2.0,
      vetAccepted: 65.0
    },
    availableLabor: {
      total:           { headcount: 407.0, hours: 2007.0 },
      vtoOpen:         { headcount: 0.0,   hours: 0.0 },
      vtoAccepted:     { headcount: 0.0,   hours: 0.0 },
      vetOpen:         { headcount: 2.0,   hours: 10.0 },    // 2 associates × ~5h VET shift
      vetAccepted:     { headcount: 65.0,  hours: 325.0 },   // 65 associates × 5h VET shift
      azReadyOpen:     { headcount: 141.0, hours: 619.0 },
      azReadyAccepted: { headcount: 71.0,  hours: 355.0 },   // 71 associates × 5h shift
      laborShare:      { headcount: 0.0,   hours: -287.0 },
      miscHours:       { headcount: 0.0,   hours: -76.0 },
    },
    totalAttendance: {
      recommended: 86.0,
      override: 0.0,
      overrideReason: '',
      overrideComment: ''
    },
    categories: [
      {
        name: 'Voluntary Extra Time',
        order: 1.0,
        associatesCount: 325.0,   // hours: VET accepted (matches availableLabor.vetAccepted.hours)
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      },
      {
        name: 'Voluntary Time Off',
        order: 2.0,
        associatesCount: 0.0,     // hours: VTO accepted (matches availableLabor.vtoAccepted.hours)
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      },
      {
        name: 'Amazon Ready',
        order: 3.0,
        associatesCount: 355.0,   // hours: Amazon Ready open + accepted combined
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      },
      {
        name: 'Labor Share',
        order: 4.0,
        associatesCount: -287.0,   // hours: negative = loaned out (matches availableLabor.laborShare.hours)
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      },
      {
        name: 'Miscellaneous Hours',
        order: 5.0,
        associatesCount: -76.0,
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      }
    ],
    overrideReasonList: [],
    departmentHeadcounts: {},
    departmentAttendances: {},
    departmentVolumeGoals: {
      'Customer Returns': 69746.0
    },
    functionPermissionHeadcounts: {}
  },
  ratesData: mockRatesData,
  planStatus: PlanStatus.PENDING_USER_INPUT
};

// Mock Target and WIP Volume Response - Real Production Data Structure
export const mockTargetAndWipVolume: GetTargetAndWIPVolumeResponse = {
  totalTargetVolume: 23756.0,
  primaryTargetAndWipVolume: [
    {
      label: 'CRETURN_PRIMARY_GRADING_TEAM_MECH',
      displayName: 'Primary Grading Team Mech',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_PRIMARY_GRADING',
      displayName: 'Primary Grading',
      targetVolume: 23756.0,
      wipVolume: 22716.0
    }
  ],
  secondaryTargetAndWipVolume: [
    {
      label: 'EXTERNAL_PS',
      displayName: 'ExtrnlRP PSolver',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'BOD',
      displayName: 'BOD',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'ADVANCED_DETECTION',
      displayName: 'Advanced Detection',
      targetVolume: 102.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_REFURB',
      displayName: 'C-Returns Refurbish',
      targetVolume: 885.0,
      wipVolume: 0.0
    },
    {
      label: 'WAREHOUSE_DEALS',
      displayName: 'WD Grading - AR',
      targetVolume: 3786.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_ADD_INS',
      displayName: 'C-Returns_Add-Ins',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'Team_Mech_Audit',
      displayName: 'Team_Mech_Audit',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_REPAIR_PREP',
      displayName: 'Repair Prep',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'WAREHOUSE_DEALS_PS',
      displayName: 'WD Problem Solve',
      targetVolume: 197.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_PS_ITEM',
      displayName: 'C-Returns Prob Solve',
      targetVolume: 642.0,
      wipVolume: 0.0
    }
  ]
};

// Mock Shift Plan Data
export const mockShiftPlan: SPMSGetShiftPlanResponse = {
  planId: 'MOCK-PLAN-001',
  nodeId: 'LAS2',
  ofdDate: '2024-02-15',
  planStatus: PlanStatus.PENDING_USER_INPUT,
  createdBy: 'testuser@amazon.com',
  createdDate: '2024-02-10T10:30:00Z',
  lastModifiedBy: 'testuser@amazon.com', 
  lastModifiedDate: '2024-02-12T14:15:00Z',
  targetVolume: 125000,
  wipVolume: 8500,
  totalScheduledHours: 2496, // 312 associates * 8 hours
  efficiency: 0.85,
  utilization: 0.92,
  totalAssociates: 260,
  dayShiftAssociates: 120,
  eveningShiftAssociates: 95,
  nightShiftAssociates: 45
};

// Mock Shift Plans List
export const mockShiftPlansList: SPMSGetShiftPlanResponse[] = [
  mockShiftPlan,
  {
    ...mockShiftPlan,
    planId: 'MOCK-PLAN-002',
    ofdDate: '2024-02-16', 
    planStatus: PlanStatus.APPROVED,
    targetVolume: 135000,
    wipVolume: 9200
  },
  {
    ...mockShiftPlan,
    planId: 'MOCK-PLAN-003',
    ofdDate: '2024-02-17',
    planStatus: PlanStatus.PENDING_APPROVAL,
    targetVolume: 118000,
    wipVolume: 7800
  }
];

// ============================================================
// Dark Mode Configuration
// Toggle this to test dark mode in local development
// ============================================================
export const MOCK_DARK_MODE_ENABLED = false; // Set to true to test dark mode

// Configuration constants - environment-aware delays
const isTestEnvironment = () => process.env.NODE_ENV === 'test' || 
                              process.env.JEST_WORKER_ID !== undefined ||
                              (global as any).__JEST_WORKER_ID__ !== undefined;

const MOCK_API_DELAY_MS = isTestEnvironment() ? 0 : 2000; // 0ms in tests, 2s in dev
const DEFAULT_DELAY_MS = isTestEnvironment() ? 0 : 100; // 0ms in tests, 100ms in dev (reduced from 500ms)

// Global mock control - allows tests to disable mock service entirely
const isMockDisabled = () => process.env.DISABLE_MOCK_DATA_SERVICE === 'true' ||
                            (global as any).__DISABLE_MOCK_DATA_SERVICE__ === true;

console.log(`[Mock Data Service] Environment: ${process.env.NODE_ENV}, Test: ${isTestEnvironment()}, Delays: ${DEFAULT_DELAY_MS}ms`);

// Polling simulation for getShiftPlanOutput - tracks request count per planId
const shiftPlanOutputRequestCounts: { [planId: string]: number } = {};

// Deterministic rotation counter for MockDataService.getWorkItems so the
// mock cycles through overflow, end-of-list, and happy-path branches.
let workItemsCallCounter = 0;

// Mock API Response Generator
export class MockDataService {
  /**
   * Simulates API response delay for realistic testing
   * Default delay is 500ms, but can be overridden for specific testing scenarios
   */
  private static async simulateDelay(ms: number = DEFAULT_DELAY_MS): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get warehouse configuration based on warehouseId or nodeId
   */
  private static getWarehouseConfig(params?: MockRequestParams) {
    const warehouseId = params?.warehouseId || params?.nodeId;
    const config = warehouseVariations[warehouseId || 'default'] || warehouseVariations['default'];
    
    console.log(`[Mock Data] Using warehouse config for: ${warehouseId || 'default'}`, config);
    return config;
  }

  /**
   * Generate dynamic mock data based on warehouse parameters
   * Uses real production data structure with warehouse-specific variations
   */
  private static generateDynamicRatesData(warehouseConfig: any): RatesData {
    // Apply warehouse-specific multipliers to the real production rates
    const warehouseMultiplier = warehouseConfig.efficiency || 0.85;
    const volumeBase = warehouseConfig.baseVolume || 125000;
    
    // Clone and modify the real production rates data with warehouse variations
    const dynamicFunctionRates = mockRatesData.functionRates.map(rate => ({
      ...rate,
      rate: rate.rate * warehouseMultiplier,
      volume: Math.round((rate.volume || 1000) * (volumeBase / 125000)) // Scale based on warehouse size
    }));

    const dynamicProcessRates = mockRatesData.processRates.map(rate => ({
      ...rate,
      rate: rate.rate * warehouseMultiplier,
      volume: Math.round(rate.volume * (volumeBase / 125000)) // Scale based on warehouse size
    }));

    // Clone and modify department groupings
    const dynamicFunctionRatesByDepartment: { [key: string]: any[] } = {};
    Object.entries(mockRatesData.functionRatesByDepartment).forEach(([dept, rates]) => {
      dynamicFunctionRatesByDepartment[dept] = rates.map(rate => ({
        ...rate,
        rate: rate.rate * warehouseMultiplier,
        volume: Math.round((rate.volume || 1000) * (volumeBase / 125000))
      }));
    });

    const dynamicProcessRatesByDepartment: { [key: string]: any[] } = {};
    Object.entries(mockRatesData.processRatesByDepartment).forEach(([dept, rates]) => {
      dynamicProcessRatesByDepartment[dept] = rates.map(rate => ({
        ...rate,
        rate: rate.rate * warehouseMultiplier,
        volume: Math.round(rate.volume * (volumeBase / 125000))
      }));
    });
    
    return {
      functionRates: dynamicFunctionRates,
      processRates: dynamicProcessRates,
      functionRatesByDepartment: dynamicFunctionRatesByDepartment,
      processRatesByDepartment: dynamicProcessRatesByDepartment
    };
  }

  /**
   * Get mock rates and roster data with dynamic parameters
   */
  static async getRatesAndRoster(params?: MockRequestParams): Promise<GetRatesAndRosterResponse> {
    await this.simulateDelay(MOCK_API_DELAY_MS);
    
    const warehouseConfig = this.getWarehouseConfig(params);
    const dynamicRatesData = this.generateDynamicRatesData(warehouseConfig);
    
    const dynamicRosterData = {
      ...mockRatesAndRoster,
      rosterData: {
        ...mockRatesAndRoster.rosterData,
        // Keep static mock headcount, totalAttendance, and categories as-is
        // so they match the rostered headcount display table
      },
      ratesData: dynamicRatesData
    };
    
    console.log(`[Mock Data] Returning dynamic rates and roster data for warehouse: ${warehouseConfig.name}`);
    return dynamicRosterData;
  }

  /**
   * Get mock target and WIP volume data with dynamic parameters
   * Uses real production data structure with dynamic scaling based on warehouse configuration
   */
  static async getTargetAndWipVolume(params?: MockRequestParams): Promise<GetTargetAndWIPVolumeResponse> {
    await this.simulateDelay(400);
    
    const warehouseConfig = this.getWarehouseConfig(params);
    const baseVolume = warehouseConfig.baseVolume;
    const scaleFactor = baseVolume / 23756.0; // Scale based on production baseline
    
    const dynamicVolumeData: GetTargetAndWIPVolumeResponse = {
      totalTargetVolume: baseVolume,
      primaryTargetAndWipVolume: [
        {
          label: 'CRETURN_PRIMARY_GRADING_TEAM_MECH',
          displayName: 'Primary Grading Team Mech',
          targetVolume: 0.0, // Keep zero values as-is
          wipVolume: 0.0
        },
        {
          label: 'CRETURN_PRIMARY_GRADING',
          displayName: 'Primary Grading',
          targetVolume: Math.round(23756.0 * scaleFactor),
          wipVolume: Math.round(22716.0 * scaleFactor)
        }
      ],
      secondaryTargetAndWipVolume: [
        {
          label: 'EXTERNAL_PS',
          displayName: 'ExtrnlRP PSolver',
          targetVolume: 0.0, // Keep zero values as-is
          wipVolume: 0.0
        },
        {
          label: 'BOD',
          displayName: 'BOD',
          targetVolume: 0.0,
          wipVolume: 0.0
        },
        {
          label: 'ADVANCED_DETECTION',
          displayName: 'Advanced Detection',
          targetVolume: Math.round(102.0 * scaleFactor),
          wipVolume: 0.0 // Keep zero values as-is
        },
        {
          label: 'CRETURN_REFURB',
          displayName: 'C-Returns Refurbish',
          targetVolume: Math.round(885.0 * scaleFactor),
          wipVolume: 0.0
        },
        {
          label: 'WAREHOUSE_DEALS',
          displayName: 'WD Grading - AR',
          targetVolume: Math.round(3786.0 * scaleFactor),
          wipVolume: 0.0
        },
        {
          label: 'CRETURN_ADD_INS',
          displayName: 'C-Returns_Add-Ins',
          targetVolume: 0.0,
          wipVolume: 0.0
        },
        {
          label: 'Team_Mech_Audit',
          displayName: 'Team_Mech_Audit',
          targetVolume: 0.0,
          wipVolume: 0.0
        },
        {
          label: 'CRETURN_REPAIR_PREP',
          displayName: 'Repair Prep',
          targetVolume: 0.0,
          wipVolume: 0.0
        },
        {
          label: 'WAREHOUSE_DEALS_PS',
          displayName: 'WD Problem Solve',
          targetVolume: Math.round(197.0 * scaleFactor),
          wipVolume: 0.0
        },
        {
          label: 'CRETURN_PS_ITEM',
          displayName: 'C-Returns Prob Solve',
          targetVolume: Math.round(642.0 * scaleFactor),
          wipVolume: 0.0
        }
      ]
    };
    
    console.log(`[Mock Data] Returning dynamic target and WIP volume data for warehouse: ${warehouseConfig.name} (scale factor: ${scaleFactor.toFixed(2)})`);
    return dynamicVolumeData;
  }

  /**
   * Get mock shift plan data with dynamic parameters
   * Supports both legacy string planId parameter and new MockRequestParams
   */
  static async getShiftPlan(paramsOrPlanId?: MockRequestParams | string): Promise<SPMSGetShiftPlanResponse> {
    await this.simulateDelay(350);
    
    // Handle backward compatibility - if string is passed, convert to params object
    let params: MockRequestParams | undefined;
    if (typeof paramsOrPlanId === 'string') {
      params = { planId: paramsOrPlanId };
    } else {
      params = paramsOrPlanId;
    }
    
    const warehouseConfig = this.getWarehouseConfig(params);
    const planId = params?.planId;
    
    if (planId) {
      const plan = mockShiftPlansList.find(p => p.planId === planId);
      if (plan) {
        const dynamicPlan = {
          ...plan,
          nodeId: warehouseConfig.nodeId,
          targetVolume: warehouseConfig.baseVolume,
          totalAssociates: warehouseConfig.associateCount,
          efficiency: warehouseConfig.efficiency,
          totalScheduledHours: warehouseConfig.associateCount * 8,
          dayShiftAssociates: Math.round(warehouseConfig.associateCount * 0.46),
          eveningShiftAssociates: Math.round(warehouseConfig.associateCount * 0.37),
          nightShiftAssociates: Math.round(warehouseConfig.associateCount * 0.17)
        };
        
        console.log(`[Mock Data] Returning dynamic shift plan for ${planId} at ${warehouseConfig.name}`);
        return dynamicPlan;
      }
    }
    
    const dynamicDefaultPlan = {
      ...mockShiftPlan,
      nodeId: warehouseConfig.nodeId,
      targetVolume: warehouseConfig.baseVolume,
      totalAssociates: warehouseConfig.associateCount,
      efficiency: warehouseConfig.efficiency,
      totalScheduledHours: warehouseConfig.associateCount * 8,
      dayShiftAssociates: Math.round(warehouseConfig.associateCount * 0.46),
      eveningShiftAssociates: Math.round(warehouseConfig.associateCount * 0.37),
      nightShiftAssociates: Math.round(warehouseConfig.associateCount * 0.17)
    };
    
    console.log(`[Mock Data] Returning dynamic default shift plan for ${warehouseConfig.name}`);
    return dynamicDefaultPlan;
  }

  /**
   * Get mock shift plans list
   */
  static async getAllShiftPlans(): Promise<SPMSGetShiftPlanResponse[]> {
    await this.simulateDelay(600);
    console.log('[Mock Data] Returning mock shift plans list');
    return [...mockShiftPlansList];
  }

  /**
   * Mock verify volumes response
   */
  static async verifyVolumes(): Promise<boolean> {
    await this.simulateDelay(800);
    console.log('[Mock Data] Returning mock verify volumes response');
    return true;
  }

  /**
   * Mock create shift plan response - simulates generating a new shift plan
   */
  static async createShiftPlan(params?: MockRequestParams): Promise<{ planId: string; status: string }> {
    await this.simulateDelay(1200); // Slightly longer delay to simulate processing
    
    const warehouseConfig = this.getWarehouseConfig(params);
    
    // Generate a new plan ID
    const timestamp = Date.now();
    const planId = `MOCK-PLAN-${timestamp}`;
    
    console.log(`[Mock Data] Creating new shift plan ${planId} for ${warehouseConfig.name}`);
    console.log(`[Mock Data] Plan parameters:`, {
      nodeId: params?.nodeId || warehouseConfig.nodeId,
      ofdDate: params?.ofdDate,
      planType: params?.planType,
      clockWindowName: params?.clockWindowName
    });
    
    return { 
      planId, 
      status: 'PENDING' // or PlanStatus.PENDING_USER_INPUT
    };
  }

  /**
   * Mock publish shift plan response
   */
  static async publishShiftPlan(planId: string): Promise<{ success: boolean; status: string; planId: string }> {
    await this.simulateDelay(1000);
    console.log(`[Mock Data] Publishing shift plan ${planId}`);
    return { success: true, status: "APPROVED", planId: planId || "mock-plan-id" };
  }

  /**
   * Mock update shift plan response - simulates updating plan with input overrides
   */
  static async updateShiftPlan(planId: string, inputOverrides?: string): Promise<any> {
    await this.simulateDelay(800);
    
    console.log(`[Mock Data] Updating shift plan ${planId} with overrides:`, inputOverrides);
    
    // Return a response matching the SPMS format from the user feedback
    const currentTime = Date.now() / 1000; // Unix timestamp
    
    return {
      autoApproveOnCreation: null,
      clockWindowName: null,
      createdAt: currentTime - 1000, // Created 1 second ago
      dependentPlanId: null,
      errorCode: null,
      errorMessage: null,
      experimentMode: null,
      input: inputOverrides || null,
      isAutoPost: null,
      isAutoRun: null,
      laborTrackingStatus: null,
      lastUpdatedBy: "mock@amazon.com",
      nodeId: "TUS1", // Default node from user feedback
      nodeType: null,
      ofdDate: "2026-02-10", // Default date from user feedback
      onlyLaborOpportunities: null,
      output: null,
      planId: planId || "mock-plan-id",
      planType: "PRE_SHIFT",
      planVersion: "STATIC_V1",
      rejectReason: null,
      shiftPlanHorizonEndTime: null,
      shiftPlanHorizonStartTime: null,
      status: "RECEIVED_USER_INPUT", // Status from user feedback
      updatedAt: currentTime
    };
  }

  /**
   * Mock get shift plan output response - simulates polling behavior
   * Returns simple status for first 3-4 requests, detailed response on 5th retry
   */
  static async getShiftPlanOutput(params?: MockRequestParams): Promise<any> {
    await this.simulateDelay(60);
    
    const planId = params?.planId || "de7eb610-a735-44d0-ad69-4c0670c2dde7";
    
    // Increment request count for this planId
    if (!shiftPlanOutputRequestCounts[planId]) {
      shiftPlanOutputRequestCounts[planId] = 0;
    }
    shiftPlanOutputRequestCounts[planId]++;
    
    const requestCount = shiftPlanOutputRequestCounts[planId];
    
    console.log(`[Mock Data] getShiftPlanOutput request #${requestCount} for plan ${planId}`);
    
    // Return simple status response for first 3-4 requests
    if (requestCount < 5) {
      return {
        planId: planId,
        planStatus: "RECEIVED_USER_INPUT",
        shiftSummary: [] // Add empty shiftSummary to prevent processShiftSummaryData errors
      };
    }
    
    // Return detailed response on 5th+ request
    const detailedResponse = {
      planId: planId,
      planStatus: "PENDING_APPROVAL",
      primaryGradingVolume: {
        recommended: { total: 19548.0, alps: 23755.0 },
        override: { total: 0.0, alps: 23755.0 }
      },
      projectedPrimaryGradingVolume: [
        { time: "07:00", recommended: 0.0 },
        { time: "08:00", recommended: 2331.0 },
        { time: "09:00", recommended: 4662.0 },
        { time: "10:00", recommended: 6993.0 },
        { time: "11:00", recommended: 8121.0 },
        { time: "12:00", recommended: 10446.0 },
        { time: "13:00", recommended: 11456.0 },
        { time: "14:00", recommended: 13934.0 },
        { time: "15:00", recommended: 15697.0 },
        { time: "16:00", recommended: 18455.0 },
        { time: "17:00", recommended: 20343.0 },
        { time: "17:30", recommended: 21766.0 }
      ],
      overrides: { total: 0.0, average: 0.0 },
      backlogProjections: {
        endOfShift: { total: 0.0, alps: 0.0 },
        endOfWeek: { total: 0.0, alps: 0.0 }
      },
      shiftTotals: {
        vetAcceptedHours: { intValue: 90.0 },
        vtoAcceptedHours: { intValue: 0.0 },
        amazonReadyAcceptedHours: { intValue: 100.0 },
        showHoursNeeded: { intValue: 238.0 },
        vto: { actual: 0.0, recommended: 0.0 },
        vet: { actual: 29.0, recommended: 0.0 }
      },
      shiftNotes: [],
      shiftTimeline: [
        { label: "Start Shift", startTime: "09:00" },
        { label: "break 1", startTime: "9:15", endTime: "9:30" }
      ],
      shiftSummary: [
        {
          heading: "headcount",
          planOutput: [
            {
              startTime: "18:00",
              endTime: "19:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 131.0, hoursRequired: 131.98729549682542 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.98280956116159 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.0 }
              ]
            },
            {
              startTime: "19:00",
              endTime: "20:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 131.0, hoursRequired: 131.98729549682542 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.98280956116159 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.0 }
              ]
            },
            {
              startTime: "20:00",
              endTime: "21:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 131.0, hoursRequired: 131.98729549682542 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.98280956116159 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.0 }
              ]
            },
            {
              startTime: "21:00",
              endTime: "22:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 63.0, hoursRequired: 63.917424229895396 },
                { label: "WD Grading", intValue: 13.0, hoursRequired: 13.066952233116444 },
                { label: "Water Spider", intValue: 6.0, hoursRequired: 6.0 },
                { label: "C-Returns_Add-Ins", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Refurbish", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Sort", intValue: 4.0, hoursRequired: 4.0 },
                { label: "WD Water Spider", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Prob Solve", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Decant Non-TI", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.0 }
              ]
            },
            {
              startTime: "22:00",
              endTime: "23:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 131.0, hoursRequired: 131.6475737141638 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.913358572482476 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.3420075747483751 }
              ]
            },
            {
              startTime: "23:00",
              endTime: "00:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 129.0, hoursRequired: 129.90192717773783 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.55648749730679 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 2.0, hoursRequired: 2.413619414559132 }
              ]
            },
            {
              startTime: "00:00",
              endTime: "01:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 129.0, hoursRequired: 129.90192717773783 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.55648749730679 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 2.0, hoursRequired: 2.413619414559132 }
              ]
            },
            {
              startTime: "01:00",
              endTime: "02:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 62.0, hoursRequired: 62.813766309667294 },
                { label: "WD Grading", intValue: 12.0, hoursRequired: 12.841326036518614 },
                { label: "Water Spider", intValue: 6.0, hoursRequired: 6.0 },
                { label: "C-Returns_Add-Ins", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Refurbish", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Sort", intValue: 4.0, hoursRequired: 4.0 },
                { label: "WD Water Spider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Prob Solve", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Decant Non-TI", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 1.0, hoursRequired: 1.1670998972875548 }
              ]
            },
            {
              startTime: "02:00",
              endTime: "03:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 129.0, hoursRequired: 129.90192717773783 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.55648749730679 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 2.0, hoursRequired: 2.413619414559132 }
              ]
            },
            {
              startTime: "03:00",
              endTime: "04:30",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 62.0, hoursRequired: 62.813766309667294 },
                { label: "WD Grading", intValue: 12.0, hoursRequired: 12.841326036518614 },
                { label: "Water Spider", intValue: 6.0, hoursRequired: 6.0 },
                { label: "C-Returns_Add-Ins", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Refurbish", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Sort", intValue: 4.0, hoursRequired: 4.0 },
                { label: "WD Water Spider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Prob Solve", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Decant Non-TI", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 1.0, hoursRequired: 1.1670998972875548 }
              ]
            }
          ]
        }
      ]
    };
    
    console.log(`[Mock Data] Returning detailed shift plan output for plan ${planId} (request #${requestCount})`);
    return detailedResponse;
  }

  /**
   * Get dark mode preference for local development
   * Reads from MOCK_DARK_MODE_ENABLED configuration toggle
   * Returns response in the same format as getDarkModeStatus() from bosonClient
   */
  static async getDarkModePreference(): Promise<{ args: { darkMode: string } }> {
    await this.simulateDelay(50);
    const darkMode = MOCK_DARK_MODE_ENABLED ? 'dark' : 'light';
    console.log(`[Mock Data] 🌙 Dark mode preference: ${darkMode} (MOCK_DARK_MODE_EN
      ABLED=${MOCK_DARK_MODE_ENABLED})`);
    return { args: { darkMode } };
  }

  /**
   * Mock trailer volume data for /listYardTrailers API
   * Returns data in ApiTrailerData format expected by the transformApiDataToTrailerData function
   */
  static async getInboundTrailerVolume(): Promise<any> {
    await this.simulateDelay(400);
    console.log('[Mock Data] Returning mock inbound trailer volume data');
    
    // Return data in ApiTrailerData format
    const mockTrailers = [
      {
        visitId: 'VISIT-001',
        trailerId: 'T-001',
        carrierId: 'FEDEX',
        location: 'BAY-A1',
        volume: {
          total: 2800,
          small: 1200,
          medium: 1000,
          large: 600,
          specialty: {
            tvs: 150,
            rugs: 75
          }
        },
        blocked: false,
        blockedReason: null,
        remainingItems: 420,
        tscore: 85.5,
        ymsNotes: 'Priority items - electronics'
      },
      {
        visitId: 'VISIT-002',
        trailerId: 'T-002',
        carrierId: 'UPS',
        location: 'BAY-B3',
        volume: {
          total: 3200,
          small: 1500,
          medium: 1200,
          large: 500,
          specialty: {
            tvs: 200,
            rugs: 100
          }
        },
        blocked: true,
        blockedReason: 'Safety inspection required',
        remainingItems: 800,
        tscore: 72.3,
        ymsNotes: 'Hold for inspection'
      },
      {
        visitId: 'VISIT-003',
        trailerId: 'T-003',
        carrierId: 'DHL',
        location: 'BAY-C2',
        volume: {
          total: 1850,
          small: 800,
          medium: 700,
          large: 350,
          specialty: {
            tvs: 80,
            rugs: 45
          }
        },
        blocked: false,
        blockedReason: null,
        remainingItems: 185,
        tscore: 91.2,
        ymsNotes: 'Express delivery items'
      },
      {
        visitId: 'VISIT-004',
        trailerId: 'T-004',
        carrierId: 'AMZL',
        location: 'BAY-D1',
        volume: {
          total: 4100,
          small: 2000,
          medium: 1500,
          large: 600,
          specialty: {
            tvs: 300,
            rugs: 150
          }
        },
        blocked: false,
        blockedReason: null,
        remainingItems: 1230,
        tscore: 88.7,
        ymsNotes: 'Amazon Logistics - standard processing'
      },
      {
        visitId: 'VISIT-005',
        trailerId: 'T-005',
        carrierId: 'XPO',
        location: 'BAY-E2',
        volume: {
          total: 2450,
          small: 1100,
          medium: 900,
          large: 450,
          specialty: {} // Empty specialty object - simulates production issue
        },
      blocked: false,
      blockedReason: null,
      remainingItems: 367,
      tscore: 79.3,
      ymsNotes: 'Standard freight - no specialty items'
    },
    {
      visitId: 'VISIT-006',
      trailerId: 'T-006',
      carrierId: 'USPS',
      location: 'BAY-F1',
      volume: {
        total: 0,
        small: 0,
        medium: 0,
        large: 0,
        specialty: { tvs: 0, rugs: 0 }
      },
      blocked: false,
      blockedReason: null,
      remainingItems: 0,
      tscore: 65.0,
      ymsNotes: 'Sealed trailer - volume unknown, needs manual entry'
    },
    {
      visitId: 'VISIT-007', trailerId: 'T-007', carrierId: 'FEDEX', location: 'BAY-G1',
      volume: { total: 1920, small: 850, medium: 720, large: 350, specialty: { tvs: 60, rugs: 30 } },
      blocked: false, blockedReason: null, remainingItems: 288, tscore: 82.1, ymsNotes: 'Standard mixed freight'
    },
    {
      visitId: 'VISIT-008', trailerId: 'T-008', carrierId: 'UPS', location: 'BAY-H2',
      volume: { total: 3650, small: 1700, medium: 1300, large: 650, specialty: { tvs: 180, rugs: 90 } },
      blocked: false, blockedReason: null, remainingItems: 912, tscore: 76.8, ymsNotes: 'Heavy electronics load'
    },
    {
      visitId: 'VISIT-009', trailerId: 'T-009', carrierId: 'DHL', location: 'BAY-A3',
      volume: { total: 2100, small: 950, medium: 800, large: 350, specialty: { tvs: 45, rugs: 20 } },
      blocked: false, blockedReason: null, remainingItems: 315, tscore: 89.4, ymsNotes: 'International returns'
    },
    {
      visitId: 'VISIT-010', trailerId: 'T-010', carrierId: 'AMZL', location: 'BAY-B1',
      volume: { total: 4500, small: 2200, medium: 1600, large: 700, specialty: { tvs: 250, rugs: 120 } },
      blocked: false, blockedReason: null, remainingItems: 1350, tscore: 93.2, ymsNotes: 'High priority - AMZL bulk'
    },
    {
      visitId: 'VISIT-011', trailerId: 'T-011', carrierId: 'XPO', location: 'BAY-C1',
      volume: { total: 1560, small: 700, medium: 580, large: 280, specialty: { tvs: 35, rugs: 15 } },
      blocked: true, blockedReason: 'RED TAGGED - safety concern', remainingItems: 234, tscore: 68.5, ymsNotes: 'RED TAGGED - unsafe dock condition'
    },
    {
      visitId: 'VISIT-012', trailerId: 'T-012', carrierId: 'FEDEX', location: 'BAY-D2',
      volume: { total: 2780, small: 1250, medium: 1050, large: 480, specialty: { tvs: 110, rugs: 55 } },
      blocked: false, blockedReason: null, remainingItems: 417, tscore: 84.7, ymsNotes: 'FedEx Ground - standard'
    },
    {
      visitId: 'VISIT-013', trailerId: 'T-013', carrierId: 'UPS', location: 'BAY-E1',
      volume: { total: 3100, small: 1400, medium: 1150, large: 550, specialty: { tvs: 170, rugs: 85 } },
      blocked: false, blockedReason: null, remainingItems: 775, tscore: 77.9, ymsNotes: 'UPS SurePost returns'
    },
    {
      visitId: 'VISIT-014', trailerId: 'T-014', carrierId: 'AMZL', location: 'BAY-F2',
      volume: { total: 0, small: 0, medium: 0, large: 0, specialty: { tvs: 0, rugs: 0 } },
      blocked: false, blockedReason: null, remainingItems: 0, tscore: 62.3, ymsNotes: 'Sealed - needs volume override'
    },
    {
      visitId: 'VISIT-015', trailerId: 'T-015', carrierId: 'DHL', location: 'BAY-G2',
      volume: { total: 1340, small: 600, medium: 500, large: 240, specialty: { tvs: 25, rugs: 10 } },
      blocked: false, blockedReason: null, remainingItems: 201, tscore: 86.1, ymsNotes: 'DHL Express returns'
    },
    {
      visitId: 'VISIT-016', trailerId: 'T-016', carrierId: 'FEDEX', location: 'BAY-H1',
      volume: { total: 2950, small: 1350, medium: 1100, large: 500, specialty: { tvs: 140, rugs: 70 } },
      blocked: false, blockedReason: null, remainingItems: 590, tscore: 81.3, ymsNotes: 'FedEx Express - priority items'
    },
    {
      visitId: 'VISIT-017', trailerId: 'T-017', carrierId: 'XPO', location: 'BAY-A2',
      volume: { total: 3800, small: 1800, medium: 1400, large: 600, specialty: { tvs: 220, rugs: 110 } },
      blocked: false, blockedReason: null, remainingItems: 950, tscore: 74.6, ymsNotes: 'YELLOW TAGGED - minor dock issue'
    },
    {
      visitId: 'VISIT-018', trailerId: 'T-018', carrierId: 'USPS', location: 'BAY-B2',
      volume: { total: 980, small: 450, medium: 360, large: 170, specialty: { tvs: 15, rugs: 8 } },
      blocked: false, blockedReason: null, remainingItems: 147, tscore: 90.5, ymsNotes: 'USPS Priority Mail returns'
    },
    {
      visitId: 'VISIT-019', trailerId: 'T-019', carrierId: 'AMZL', location: 'BAY-C3',
      volume: { total: 5200, small: 2500, medium: 1900, large: 800, specialty: { tvs: 350, rugs: 175 } },
      blocked: false, blockedReason: null, remainingItems: 1560, tscore: 95.0, ymsNotes: 'AMZL - highest priority bulk'
    },
    {
      visitId: 'VISIT-020', trailerId: 'T-020', carrierId: 'UPS', location: 'BAY-D3',
      volume: { total: 2200, small: 1000, medium: 830, large: 370, specialty: { tvs: 95, rugs: 48 } },
      blocked: false, blockedReason: null, remainingItems: 550, tscore: 78.2, ymsNotes: 'UPS Ground - mixed returns'
    },
    {
      visitId: 'VISIT-021', trailerId: 'T-021', carrierId: 'FEDEX', location: 'BAY-E3',
      volume: { total: 1680, small: 750, medium: 630, large: 300, specialty: { tvs: 50, rugs: 25 } },
      blocked: false, blockedReason: null, remainingItems: 252, tscore: 83.9, ymsNotes: 'FedEx SmartPost returns'
    }
  ];

    return {
      trailers: mockTrailers
    };
  }

  /**
   * Returns sample indirect staffing configuration data.
   */
  static async getIndirectStaffingConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    return {
      warehouseId: params?.warehouseId || 'LAS2',
      indirectStaffingRules: [
        {
          department: 'Reverse Logistics',
          processPath: 'C-Returns Support',
          function: 'C-Returns Lead/PA',
          directRoleMapping: 'Customer Returns',
          description: 'Lead/PA support for C-Returns',
          ruleList: [
            {
              priority: 1,
              ruleType: 'FOR_EVERY' as const,
              ruleAppliesOn: 'Shift',
              directBaseRatio: 15,
              indirectRequirementRatio: 1,
              lever: null,
            },
          ],
        },
        {
          department: 'Inbound',
          processPath: 'Transfer In and Stow',
          function: 'Water Spider',
          directRoleMapping: 'Stow Associates',
          description: 'Water spider support for stow',
          ruleList: [
            {
              priority: 1,
              ruleType: 'ALWAYS_HAVE' as const,
              ruleAppliesOn: 'N/A',
              directBaseRatio: null,
              indirectRequirementRatio: 2,
              lever: null,
            },
          ],
        },
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Support',
          function: 'Team_Mech_P-Solver',
          directRoleMapping: 'Primary Grading',
          description: 'Problem solve on the east side of the building. They are to process work that gets escalated to them.',
          ruleList: [
            {
              priority: 1,
              ruleType: 'FOR_EVERY' as const,
              ruleAppliesOn: 'TEAM_MECH_AA',
              directBaseRatio: 20,
              indirectRequirementRatio: 2,
              lever: null,
            },
          ],
        },
      ],
    };
  }

  /**
   * Returns a success response for updating indirect staffing configuration.
   */
  static async updateIndirectStaffingConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);
    return {
      status: 'PROMOTED',
      stageVersionName: (params?.warehouseId || 'LAS2') + '-mockuser-20260413143526',
      message: 'Changes promoted to live config',
      processedCount: params?.changes?.length || 1,
      failedCount: 0
    };
  }

  /**
   * Returns mock drafts list for a warehouse.
   */
  static async listConfigDrafts(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    const wh = params?.warehouseId || 'LAS2';
    return {
      warehouseId: wh,
      configType: params?.configType || 'INDIRECT_STAFFING',
      drafts: [
        {
          stageVersionName: wh + '-dhaanjay-20260413143526',
          createdBy: 'dhaanjay@amazon.com',
          createdAt: '2026-04-13T14:35:26Z',
          changeCount: 2,
        },
        {
          stageVersionName: wh + '-johndoe-20260412091500',
          createdBy: 'johndoe@amazon.com',
          createdAt: '2026-04-12T09:15:00Z',
          changeCount: 1,
        },
      ],
    };
  }

  /**
   * Returns mock promote response.
   */
  static async promoteConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);
    return {
      status: 'SUCCESS',
      message: 'Successfully promoted ' + (params?.stageVersionName || 'draft') + ' to live config',
    };
  }

  /**
   * Returns mock flow path percentage configuration for a warehouse.
   */
  static async getFlowPathPercentageConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    const warehouseId = params?.warehouseId || 'LAS2';
    return {
      flowPathEntries: [
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Processed',
          function: 'Advanced Grading Lite 1',
          fclmCode: 4300016841,
          flowPathPercentage: 15,
        },
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Processed',
          function: 'Advanced Grading Lite 2',
          fclmCode: 4300016839,
          flowPathPercentage: 10,
        },
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Processed',
          function: 'MLG Fast Processing',
          fclmCode: 1766442445256,
          flowPathPercentage: 25,
        },
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Processed',
          function: 'Advanced Grading Plus',
          fclmCode: 1617058767035,
          flowPathPercentage: 20,
        },
        {
          department: 'Warehouse Deals',
          processPath: 'WHD Grading',
          function: 'WD Grading - AR',
          fclmCode: 4300006832,
          flowPathPercentage: 30,
        },
      ],
    };
  }

  /**
   * Mock response for GetWorkItemCounts.
   *
   * `pendingTasksCount` and `highPriorityCount` are derived as the sum of
   * their per-process-path counterparts so the dashboard totals reconcile
   * with the per-path cards.
   */
  static async getWorkItemCounts(
    _params?: MockRequestParams
  ): Promise<GetWorkItemCountsResponse> {
    await this.simulateDelay(DEFAULT_DELAY_MS);
    console.log('[Mock Data] Returning mock work item counts');

    const processPathCounts = [
      { processPath: 'Yard', totalCount: 12, highPriorityCount: 3 },
      { processPath: 'Problem Solve', totalCount: 20, highPriorityCount: 4 },
      { processPath: 'Clean Decant', totalCount: 15, highPriorityCount: 2 },
      { processPath: 'Warehouse Deals', totalCount: 7, highPriorityCount: 1 },
    ];

    const pendingTasksCount = processPathCounts.reduce(
      (acc, entry) => acc + entry.totalCount,
      0,
    );
    const highPriorityCount = processPathCounts.reduce(
      (acc, entry) => acc + entry.highPriorityCount,
      0,
    );

    return {
      pendingTasksCount,
      highPriorityCount,
      processPathCounts,
      completedTodayCount: 132,
      expiredTodayCount: 6,
    };
  }

  /**
   * Returns a success response for updating warehouse operational configuration.
   */
  static async updateWarehouseOperationalConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    const changeCount = params?.changes?.length || 1;
    return {
      status: 'PROMOTED',
      stageVersionName: (params?.warehouseId || 'LAS2') + '-mockuser-' + Date.now(),
      message: changeCount + ' changes promoted to live config',
      processedCount: changeCount,
      failedCount: 0,
    };
  }

  /**
   * Mock response for GetWorkItems.
   *
   * Cycles deterministically through three branches so local dev exercises
   * every path in the frontend's buffer logic:
   *   - overflow   (every 3rd call)  → pageSize + 3 items with a nextToken
   *   - end-of-list(every 5th call)  → fewer than pageSize items, no nextToken
   *   - happy path (all others)      → exactly pageSize items with a nextToken
   *
   * Passing `nextToken === "final"` forces the end-of-list branch on demand.
   */
  static async getWorkItems(params: {
    processPaths?: string[];
    pageSize?: number;
    nextToken?: string;
  }): Promise<GetWorkItemsResponse> {
    await this.simulateDelay(DEFAULT_DELAY_MS);

    const pageSize = params?.pageSize ?? WORK_ITEMS_PAGE_SIZE;
    const processPaths = params?.processPaths ?? [];
    const callNumber = ++workItemsCallCounter;

    // Branch selection:
    //   - forced end-of-list when caller passes nextToken === "final"
    //   - end-of-list on every 5th call
    //   - overflow on every 3rd call (but not when it overlaps with 5th)
    //   - happy path otherwise
    const defaultProcessPathRotation = ['Yard', 'Problem Solve', 'Clean Decant', 'Warehouse Deals'];
    const severityRotation = ['HIGH', 'MEDIUM', 'LOW'];

    const isForcedEnd = params?.nextToken === 'final';
    const isEndOfList = isForcedEnd || callNumber % 5 === 0;
    const isOverflow = !isEndOfList && callNumber % 3 === 0;

    let itemCount: number;
    if (isEndOfList) {
      // Fewer than pageSize items. Use max(1, pageSize - 3) so we still have
      // some rows to render; drop to 0 when the caller specifies pageSize <= 1.
      itemCount = Math.max(0, Math.min(pageSize - 3, pageSize - 1));
    } else if (isOverflow) {
      itemCount = pageSize + 3;
    } else {
      itemCount = pageSize;
    }

    const workItems: WorkItemSummary[] = [];
    for (let i = 0; i < itemCount; i++) {
      const processPath = processPaths.length > 0
        ? processPaths[0]
        : defaultProcessPathRotation[i % defaultProcessPathRotation.length];

      const severity = severityRotation[(callNumber + i) % severityRotation.length];

      // lastWorkItemStatusUpdatedTime spans the last 0–20 hours so the AGE
      // column renders a wide distribution of values in dev. Random is
      // acceptable for a dev-only mock.
      const hoursAgo = Math.floor(Math.random() * 20);
      const minutesAgo = Math.floor(Math.random() * 60);
      const lastWorkItemStatusUpdatedTime =
        Date.now() - (hoursAgo * 60 * 60 * 1000 + minutesAgo * 60 * 1000);

      // Operator-mix invariant for the Work Item Detail Page flow:
      // every non-empty batch emits at least one row with an
      // empty operator (exercises the "Start Task" path) and at least one
      // row with a populated operator (exercises the "Continue Task"
      // path). Indices 0 and 1 are dedicated to those two cases; the
      // remaining rows rotate through `mock.operator1..5` to keep
      // operator variety visible in dev.
      let operator: string;
      if (i === 0) {
        operator = '';
      } else if (i === 1) {
        operator = 'mock.operator.alice';
      } else {
        operator = `mock.operator${(i % 5) + 1}`;
      }

      workItems.push({
        workItemId: `mock-item-${callNumber}-${i}`,
        severity,
        exceptionName: `Mock exception ${callNumber}-${i}`,
        processPath,
        operator,
        lastWorkItemStatusUpdatedTime,
      });
    }

    const response: GetWorkItemsResponse = {
      workItems,
      ...(isEndOfList ? {} : { nextToken: `mock-next-token-${callNumber}` }),
    };

    console.log(
      `[Mock Data] getWorkItems call #${callNumber}: branch=${
        isEndOfList ? 'end-of-list' : isOverflow ? 'overflow' : 'happy'
      }, items=${workItems.length}, nextToken=${response.nextToken ?? 'null'}`
    );

    return response;
  }

  /**
   * Test-only helper: resets the deterministic rotation counter used by
   * `getWorkItems` so test cases can start from a known state.
   */
  static __resetWorkItemsCallCounter(): void {
    workItemsCallCounter = 0;
  }

  /**
   * Mock response for `LoadWorkItem`.
   *
   * Resolves any `workItemId` to a fully populated `WorkItem` shape so the
   * Start → detail-page flow is exercisable end-to-end against mocks.
   * Finite epoch-ms timestamps keep the
   * `Timestamp_Formatter` from rendering "—" placeholders in dev.
   *
   * The resolver is deterministic: for a given `workItemId`, the same
   * payload is returned every call. Unknown ids (outside the
   * `mock-item-*` family emitted by `getWorkItems`) fall through to a
   * generic shape-valid stub with the same invariants.
   */
  static async loadWorkItem(workItemId: string): Promise<LoadWorkItemResponse> {
    await this.simulateDelay(DEFAULT_DELAY_MS);

    // Hash-ish derivation so the stub varies per id but stays stable for
    // repeat calls with the same id.
    let hash = 0;
    for (let i = 0; i < workItemId.length; i++) {
      hash = (hash * 31 + workItemId.charCodeAt(i)) | 0;
    }
    const positive = Math.abs(hash);

    const severityRotation = ['HIGH', 'MEDIUM', 'LOW'];
    const processRotation = ['Yard', 'Problem Solve', 'Clean Decant', 'Warehouse Deals'];
    const grainRotation = ['Order', 'Shipment', 'Unit', 'Container'];

    // Fixed epoch-ms anchor + per-id offsets so the payload is
    // deterministic and the three timestamps form a chronological
    // sequence (created < updated < lastStatusUpdated).
    const anchor = Date.UTC(2024, 0, 15, 9, 30, 45); // 2024-01-15 09:30:45 UTC
    const createdTime = anchor + (positive % 100) * 60 * 1000;
    const updatedTime = createdTime + 15 * 60 * 1000;
    const lastWorkItemStatusUpdatedTime = updatedTime + 15 * 60 * 1000;

    const workItem: WorkItem = {
      workItemId,
      exceptionName: `Mock exception for ${workItemId}`,
      severity: severityRotation[positive % severityRotation.length],
      status: 'ACTIVE',
      processPath: processRotation[positive % processRotation.length],
      process: processRotation[(positive + 1) % processRotation.length],
      operator: positive % 2 === 0 ? '' : 'mock.operator.alice',
      grainType: grainRotation[positive % grainRotation.length],
      grainId: `GRAIN-${positive % 10_000}`,
      actionStatus: 'OPEN',
      description: `Investigate mock exception ${workItemId}.`,
      createdTime,
      updatedTime,
      lastWorkItemStatusUpdatedTime,
      additionalAttributes: {
        lane: `LANE-${(positive % 12) + 1}`,
        batch: `BATCH-${(positive % 99) + 1}`,
        sourceSystem: 'MockDataService',
      },
    };

    console.log(
      `[Mock Data] loadWorkItem resolved workItemId=${workItemId}`,
    );

    return { workItem };
  }

  /**
   * Mock response for `UpdateWorkItemState`.
   *
   * Returns a representative success payload for both the "Start/Continue"
   * (ACTIVE) and "Resolve" (RESOLVED) call shapes. The method
   * intentionally never fails in mock mode — error-path coverage is
   * exercised by the frontend tests that mock `fetchUpdateWorkItemState`
   * directly.
   */
  static async updateWorkItemState(
    body: UpdateWorkItemStateRequest,
  ): Promise<UpdateWorkItemStateResponse> {
    await this.simulateDelay(DEFAULT_DELAY_MS);

    console.log(
      `[Mock Data] updateWorkItemState workItemId=${body.workItemId}, status=${body.status}, operator=${body.operator}`,
    );

    return {
      message: `Mock updateWorkItemState succeeded for ${body.workItemId}`,
      responseStatus: 'SUCCESS',
       };
      }
  /**
   * Returns mock compare flow monitoring data for local testing.
   */
  static async compareFlowMonitoringData(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    return {
      compareFlowMonitoringMetadata: { baselineReferenceType: 'LATEST_SHIFT_PLAN', baselineShiftPlanId: 'mock-plan-id-123' },
      shiftTotalsComparison: [
        {
          monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } },
          monitoringEntityType: 'PROCESS_PATH',
          metricsComparisonList: [
            { metricName: 'UPH', actualValue: 18.66, expectedValue: 36.6, delta: -17.94, variancePercentage: -49.01, comparisonStatus: 'CRITICAL' },
            { metricName: 'VOLUME_PROCESSED', actualValue: 12148.0, expectedValue: 498021.85, delta: -485873.85, variancePercentage: -97.56, comparisonStatus: 'CRITICAL' },
            { metricName: 'HEADCOUNT', actualValue: 62.0, expectedValue: 1296.0, delta: -1234.0, variancePercentage: -95.22, comparisonStatus: 'CRITICAL' },
          ],
        },
      ],
      hourlyShiftComparisonDataList: [
        {
          startTime: '09:00', endTime: '10:00',
          monitoringEntityIdentitiesComparisonDataList: [
            {
              monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } },
              monitoringEntityType: 'PROCESS_PATH',
              metricsComparisonList: [
                { metricName: 'UPH', actualValue: 17.28, expectedValue: 36.6, delta: -19.32, variancePercentage: -52.79, comparisonStatus: 'CRITICAL' },
                { metricName: 'VOLUME_PROCESSED', actualValue: 1365.0, expectedValue: 5562.85, delta: -4197.85, variancePercentage: -75.46, comparisonStatus: 'CRITICAL' },
                { metricName: 'HEADCOUNT', actualValue: 79.0, expectedValue: 152.0, delta: -73.0, variancePercentage: -48.03, comparisonStatus: 'CRITICAL' },
              ],
            },
          ],
        },
        {
          startTime: '10:00', endTime: '11:00',
          monitoringEntityIdentitiesComparisonDataList: [
            {
              monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } },
              monitoringEntityType: 'PROCESS_PATH',
              metricsComparisonList: [
                { metricName: 'UPH', actualValue: 17.46, expectedValue: 36.6, delta: -19.14, variancePercentage: -52.3, comparisonStatus: 'CRITICAL' },
                { metricName: 'VOLUME_PROCESSED', actualValue: 995.0, expectedValue: 5343.27, delta: -4348.27, variancePercentage: -81.38, comparisonStatus: 'CRITICAL' },
                { metricName: 'HEADCOUNT', actualValue: 57.0, expectedValue: 146.0, delta: -89.0, variancePercentage: -60.96, comparisonStatus: 'CRITICAL' },
              ],
            },
          ],
        },
      ],
    };
  }

  static async getFlowMonitoringData(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    return {
      shiftTotals: [
        {
          monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } },
          monitoringEntityType: 'PROCESS_PATH',
          metricDataList: [
            { metricName: 'UPH', metricValue: 18.66 },
            { metricName: 'VOLUME_PROCESSED', metricValue: 12148.0 },
            { metricName: 'HEADCOUNT', metricValue: 62.0 },
          ],
        },
        {
          monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } },
          monitoringEntityType: 'PROCESS_PATH',
          metricDataList: [
            { metricName: 'UPH', metricValue: 27.36 },
            { metricName: 'VOLUME_PROCESSED', metricValue: 2298.0 },
            { metricName: 'HEADCOUNT', metricValue: 8.0 },
          ],
        },
      ],
      hourlyShiftMonitoringDataList: [
        { startTime: '07:00', endTime: '08:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 12.5 }, { metricName: 'VOLUME_PROCESSED', metricValue: 450.0 }, { metricName: 'HEADCOUNT', metricValue: 36.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 22.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 88.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '08:00', endTime: '09:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 14.2 }, { metricName: 'VOLUME_PROCESSED', metricValue: 710.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 30.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 120.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '09:00', endTime: '10:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 16.8 }, { metricName: 'VOLUME_PROCESSED', metricValue: 840.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 35.5 }, { metricName: 'VOLUME_PROCESSED', metricValue: 142.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '10:00', endTime: '11:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 18.1 }, { metricName: 'VOLUME_PROCESSED', metricValue: 905.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 40.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 160.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '11:00', endTime: '12:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 19.4 }, { metricName: 'VOLUME_PROCESSED', metricValue: 970.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 42.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 168.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '12:00', endTime: '13:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 17.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 850.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 38.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 152.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        {
          startTime: '13:00', endTime: '14:00',
          monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 20.1 }, { metricName: 'VOLUME_PROCESSED', metricValue: 1005.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 45.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 180.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
          ],
        },
        {
          startTime: '14:00', endTime: '15:00',
          monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 15.31 }, { metricName: 'VOLUME_PROCESSED', metricValue: 1041.0 }, { metricName: 'HEADCOUNT', metricValue: 68.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 54.75 }, { metricName: 'VOLUME_PROCESSED', metricValue: 219.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
          ],
        },
        {
          startTime: '15:00', endTime: '16:00',
          monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 20.39 }, { metricName: 'VOLUME_PROCESSED', metricValue: 1692.0 }, { metricName: 'HEADCOUNT', metricValue: 83.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 37.88 }, { metricName: 'VOLUME_PROCESSED', metricValue: 303.0 }, { metricName: 'HEADCOUNT', metricValue: 8.0 }] },
          ],
        },
        {
          startTime: '16:00', endTime: '17:00',
          monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 17.28 }, { metricName: 'VOLUME_PROCESSED', metricValue: 1365.0 }, { metricName: 'HEADCOUNT', metricValue: 79.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 25.4 }, { metricName: 'VOLUME_PROCESSED', metricValue: 254.0 }, { metricName: 'HEADCOUNT', metricValue: 10.0 }] },
          ],
        },
      ],
    };
  }

  /*
   * Mock get shift rotation data
   * Returns a list of associates with rotation priorities and process path assignments
   */
  static async getShiftRoster(params?: MockRequestParams): Promise<GetShiftRosterResponse> {
    await this.simulateDelay();

    const warehouseConfig = this.getWarehouseConfig(params);

    const processPaths = [
      'AFE Pack', 'Inbound Receive', 'SLAM', 'Pick', 'Stow',
      'Decant', 'Problem Solve', 'Rebin', 'Pack Singles', 'Pack Multi',
    ];

    const pickPaths = (count: number): string[] => {
      const shuffled = [...processPaths].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, Math.max(1, Math.min(count, processPaths.length)));
    };

    const employees: ShiftRosterEmployee[] = [
      { alias: 'mgarcia', fullName: 'Garcia, Maria', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'rjohnson', fullName: 'Johnson, Robert', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'swilliams', fullName: 'Williams, Sarah', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
      { alias: 'jbrown', fullName: 'Brown, James', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'pjones', fullName: 'Jones, Patricia', capabilities: pickPaths(4), disallowedPaths: pickPaths(3) },
      { alias: 'mdavis', fullName: 'Davis, Michael', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'jmiller', fullName: 'Miller, Jennifer', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'dwilson', fullName: 'Wilson, David', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'lmoore', fullName: 'Moore, Linda', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
      { alias: 'rtaylor', fullName: 'Taylor, Richard', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'banderson', fullName: 'Anderson, Barbara', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'cthomas', fullName: 'Thomas, Charles', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'sjackson', fullName: 'Jackson, Susan', capabilities: pickPaths(4), disallowedPaths: pickPaths(3) },
      { alias: 'jwhite', fullName: 'White, Joseph', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'jharris', fullName: 'Harris, Jessica', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
      { alias: 'dmartin', fullName: 'Martin, Daniel', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'kthompson', fullName: 'Thompson, Karen', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'mmartinez', fullName: 'Martinez, Mark', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'nrobinson', fullName: 'Robinson, Nancy', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
      { alias: 'sclark', fullName: 'Clark, Steven', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'lrodriguez', fullName: 'Rodriguez, Lisa', capabilities: pickPaths(4), disallowedPaths: pickPaths(3) },
      { alias: 'plewis', fullName: 'Lewis, Paul', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'blee', fullName: 'Lee, Betty', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'awalker', fullName: 'Walker, Andrew', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'dhall', fullName: 'Hall, Dorothy', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
    ];

    console.log(`[Mock Data] Returning shift roster data with ${employees.length} employees for ${warehouseConfig.name}`);

    return {
      employees,
      totalCount: employees.length,
      pageNumber: 1,
      totalPages: Math.ceil(employees.length / 15),
      limit: 15,
    };
  }

  /*
   * Mock get assignments data
   * Returns a GetAssignmentsResponse with ~30 associates distributed across
   * 4 process paths (Primary, Clean decant, Resale, Problem solve). The
   * returned response satisfies the location-exclusivity invariant: every
   * associate in `response.associates` appears in at most one workstation
   * or indirect-path list.
   */
  static async getAssignments(params?: MockRequestParams): Promise<GetAssignmentsResponse> {
    await this.simulateDelay();

    const warehouseConfig = this.getWarehouseConfig(params);

    const availableTrainedPaths = [
      'Primary',
      'Clean decant',
      'Resale',
      'Problem solve',
      'Pick',
      'Stow',
    ];

    // Deterministic trained-paths picker so the mock is stable across calls.
    const pickTrainedPaths = (seed: number, count: number): string[] => {
      const paths: string[] = [];
      for (let i = 0; i < count; i += 1) {
        paths.push(availableTrainedPaths[(seed + i) % availableTrainedPaths.length]);
      }
      return paths;
    };

    // Roster of 30 associates. First 24 are clocked in; last 6 are not
    // clocked in (and will also be unassigned). 5 carry warnings.
    const warningIds = new Set(['A005', 'A011', 'A019', 'A023', 'A027']);
    const names = [
      'Garcia, Maria',    // A001
      'Johnson, Robert',  // A002
      'Williams, Sarah',  // A003
      'Brown, James',     // A004
      'Jones, Patricia',  // A005
      'Davis, Michael',   // A006
      'Miller, Jennifer', // A007
      'Wilson, David',    // A008
      'Moore, Linda',     // A009
      'Taylor, Richard',  // A010
      'Anderson, Barbara',// A011
      'Thomas, Charles',  // A012
      'Jackson, Susan',   // A013
      'White, Joseph',    // A014
      'Harris, Jessica',  // A015
      'Martin, Daniel',   // A016
      'Thompson, Karen',  // A017
      'Martinez, Mark',   // A018
      'Robinson, Nancy',  // A019
      'Clark, Steven',    // A020
      'Rodriguez, Lisa',  // A021
      'Lewis, Paul',      // A022
      'Lee, Betty',       // A023
      'Walker, Andrew',   // A024
      'Hall, Dorothy',    // A025
      'Allen, Kevin',     // A026
      'Young, Sandra',    // A027
      'King, Brian',      // A028
      'Wright, Donna',    // A029
      'Scott, Edward',    // A030
    ];

    // Build the canonical associates list. `assignedWorkstationId` is set
    // below once each associate's target location has been decided.
    const associates: AssignmentAssociate[] = names.map((name, idx) => {
      const seq = idx + 1;
      const associateId = `A${String(seq).padStart(3, '0')}`;
      // 2-4 trained paths, varied deterministically.
      const trainedPathCount = 2 + (idx % 3); // 2, 3, or 4
      return {
        associateId,
        associateName: name,
        isClocked: seq <= 24,
        hasWarning: warningIds.has(associateId),
        trainedPaths: pickTrainedPaths(idx, trainedPathCount),
      };
    });

    // Helper: fetch an associate by id from the canonical list. Throws if
    // the id is missing, which keeps the mock-construction logic honest.
    const lookup = (associateId: string): AssignmentAssociate => {
      const found = associates.find(a => a.associateId === associateId);
      if (!found) {
        throw new Error(`Mock roster is missing associate ${associateId}`);
      }
      return found;
    };

    // Helper: mark an associate as assigned to a given workstation. The
    // `assignedWorkstationId` convenience marker on the canonical entry is
    // kept in sync. Returns a shallow copy for insertion into the
    // workstation's `assignedAssociates` list.
    const assignTo = (associateId: string, workstationId: string): AssignmentAssociate => {
      const canonical = lookup(associateId);
      canonical.assignedWorkstationId = workstationId;
      return { ...canonical };
    };

    // Primary path: 8 workstations, 15 assigned associates.
    // Distribution: lines 1-7 get 2 each (14), line 8 gets 1 → 15 total.
    const primaryLineAssignments: string[][] = [
      ['A001', 'A002'], // Primary line 1
      ['A003', 'A004'], // Primary line 2
      ['A005', 'A006'], // Primary line 3
      ['A007', 'A008'], // Primary line 4
      ['A009', 'A010'], // Primary line 5
      ['A011', 'A012'], // Primary line 6
      ['A013', 'A014'], // Primary line 7
      ['A015'],         // Primary line 8
    ];
    const primaryWorkstations: Workstation[] = primaryLineAssignments.map((ids, i) => {
      const workstationId = `PRIMARY-${i + 1}`;
      return {
        workstationId,
        workstationName: `Primary line ${i + 1}`,
        capacity: 3,
        assignedAssociates: ids.map(id => assignTo(id, workstationId)),
      };
    });

    // Clean decant: 1 workstation, 4 assigned associates.
    const cleanDecantWorkstationId = 'CLEAN-DECANT-1';
    const cleanDecantWorkstations: Workstation[] = [
      {
        workstationId: cleanDecantWorkstationId,
        workstationName: 'Clean decant 1',
        capacity: 6,
        assignedAssociates: ['A016', 'A017', 'A018', 'A019'].map(id =>
          assignTo(id, cleanDecantWorkstationId)
        ),
      },
    ];

    // Resale: 1 workstation, 3 assigned associates.
    const resaleWorkstationId = 'RESALE-1';
    const resaleWorkstations: Workstation[] = [
      {
        workstationId: resaleWorkstationId,
        workstationName: 'Resale 1',
        capacity: 4,
        assignedAssociates: ['A020', 'A021', 'A022'].map(id => assignTo(id, resaleWorkstationId)),
      },
    ];

    // Problem solve (indirect): 0 workstations, 2 indirect associates.
    // Indirect associates are not tied to a workstation, so we don't set
    // `assignedWorkstationId` on them.
    const problemSolveIndirect: AssignmentAssociate[] = ['A023', 'A024'].map(id => ({
      ...lookup(id),
    }));

    const processPaths: ProcessPath[] = [
      {
        processPathId: 'PRIMARY',
        processPathName: 'Primary',
        pathType: 'direct',
        metrics: {
          totalFlow: 1200,
          actual: 15,
          assigned: 15,
          delta: 0,
          avgRatePerHour: 150,
        },
        workstations: primaryWorkstations,
      },
      {
        processPathId: 'CLEAN_DECANT',
        processPathName: 'Clean decant',
        pathType: 'direct',
        metrics: {
          totalFlow: 300,
          actual: 4,
          assigned: 4,
          delta: 0,
          avgRatePerHour: 75,
        },
        workstations: cleanDecantWorkstations,
      },
      {
        processPathId: 'RESALE',
        processPathName: 'Resale',
        pathType: 'direct',
        metrics: {
          totalFlow: 200,
          actual: 3,
          assigned: 3,
          delta: 0,
          avgRatePerHour: 65,
        },
        workstations: resaleWorkstations,
      },
      {
        processPathId: 'PROBLEM_SOLVE',
        processPathName: 'Problem solve',
        pathType: 'indirect',
        metrics: {
          actual: 2,
          assigned: 2,
          delta: 0,
          dr: 0.5,
          avgScanRate: 30,
          unitsPerHour: 40,
          processedPackages: 120,
        },
        workstations: [],
        indirectAssociates: problemSolveIndirect,
      },
    ];

    // Top-level metrics derived from the assigned distribution above.
    //   isClocked: true  → 24 (A001–A024)
    //   isClocked: false → 6  (A025–A030, all unassigned)
    //   assigned: 15 Primary + 4 Clean decant + 3 Resale + 2 Problem solve = 24
    const metrics: AssignmentsMetrics = {
      totalUnlocked: 6,
      totalClockedIn: 24,
      totalActual: associates.length,
      assignedPrimary: 15,
      assignedProblemSolve: 2,
      assignedResale: 3,
      assignedCleanDecant: 4,
      assignedIndirect: 0,
    };

    console.log(
      `[Mock Data] Returning assignments data with ${associates.length} associates across ${processPaths.length} process paths for ${warehouseConfig.name}`
    );

    return {
      associates,
      processPaths,
      metrics,
      lastModifiedBy: 'moultn@',
      lastModifiedDate: '01/14/2026 10:35 AM',
    };
  }

  static async queryAgent(params?: MockRequestParams): Promise<{ answer: string; nodeId: string }> {
    const nodeId = params?.nodeId || 'TUS1';
    const question = (params?.question || '').toLowerCase();

    await new Promise(resolve => setTimeout(resolve, 1200));

    if (question.includes('rate') || question.includes('grading')) {
      return {
        answer: `## Primary Grading Rate — ${nodeId} (10:00–11:00)\n\n| Process Path | Function | UPH | Volume | HC |\n|---|---|---:|---:|---:|\n| C-Returns Processed | Primary Grading | **35.8** | **2,542** | **71** |\n| C-Returns Processed | Primary Grading | **36.9** | **2,657** | **72** |\n\nYour plan target is **38.6 UPH**, so you're at **95.5% to plan**. The gap is ~2.7 UPH below target.\n\n> **Tip:** Check if any associates have high inferred time — that's often the biggest controllable factor.\n\n_Source: FCLM | Shift Plan_`,
        nodeId,
      };
    }

    if (question.includes('water spider') || question.includes('ws')) {
      return {
        answer: `## Water Spider Staffing — ${nodeId}\n\nBased on the standard staffing ratio for **Water Spiders**:\n\n- **Sort sites**: 1 WS per 21 direct associates (Full Case) or 1:14 (Team/Mech)\n- **CALM Code**: \`CRSDCNTF\`\n\nWith your current **71 Primary Grading** associates:\n→ Recommended: **71 ÷ 21 = 4 Water Spiders** (Full Case)\n\nYour shift plan shows **9 Water Spiders** currently staffed — above standard ratio. Additional WS may cover stow sweep or transport.\n\n_Source: Operations KB | Shift Plan_`,
        nodeId,
      };
    }

    if (question.includes('plan') || question.includes('on plan')) {
      return {
        answer: `## Plan Comparison — ${nodeId} Day Shift\n\n| Function | Plan Rate | Actual Rate | % to Plan |\n|---|---|---:|---:|\n| Primary Grading | 38.6 | 36.2 | **93.8%** |\n| WHD Grading | 24.0 | 22.8 | **95.0%** |\n| Transfer Out | 1,058 | 1,131 | **106.9%** |\n\nOverall you're slightly below plan on grading functions. Transfer Out is exceeding target.\n\n> **Recommendation:** Rebalance 1-2 associates from Transfer Out to Primary Grading to close the gap.\n\n_Source: Shift Plan | FCLM_`,
        nodeId,
      };
    }

    if (question.includes('override')) {
      return {
        answer: `## Active Overrides — ${nodeId} Day Shift\n\n| Override | Original | Current | Set By |\n|---|---|---|---|\n| Primary Grading HC | 74 | **71** | moultn@ |\n| WS Ratio | 1:21 | **1:14** | ratheer@ |\n\nThese overrides were applied during shift plan review. The WS ratio override explains why you have 9 Water Spiders instead of the standard 4.\n\n_Source: Shift Plan (SPMS)_`,
        nodeId,
      };
    }

    if (question.includes('buffer') || question.includes('wip')) {
      return {
        answer: `## Buffer Status — ${nodeId}\n\n| Buffer | Current | Target | Status |\n|---|---:|---:|---|\n| Pre-Grade | **2,847** | 3,000 | On track |\n| Post-Grade | **1,203** | 1,500 | Below target |\n| WHD | **456** | 400 | Slightly over |\n\nPost-Grade buffer is running low. At current rates, it will be depleted in approximately **45 minutes** without inbound replenishment.\n\n> **Tip:** Monitor inbound trailer ETA — next trailer expected at 15:15.\n\n_Source: FCLM_`,
        nodeId,
      };
    }

    return {
      answer: `I can help you with information about **${nodeId}**. Try asking about:\n\n- Current rates and UPH\n- Staffing ratios (water spiders, problem solve)\n- Plan comparison ("Am I on plan?")\n- Active overrides\n- Buffer/WIP status\n- Volume projections\n\nWhat would you like to know?\n\n_Source: Operations KB_`,
      nodeId,
    };
  }
}
